/* eslint-disable react/jsx-no-bind */
import { useState } from "react";
import { useMutation } from "@apollo/client";
import * as pdfjs from "pdfjs-dist";
import { useNavigate } from "react-router-dom";
import classnames from "classnames";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faLightbulb } from "@fortawesome/sharp-regular-svg-icons";
import UploadDragDrop from "@/components/forms/UploadDragDrop.tsx";
import ReferralForm, { ReferralFormInput } from "./ReferralForm";
import Page from "@/components/layout/Page";
import { gql } from "@/__generated__";
import Loader from "@/components/shared/Loader";
import { uploadFiles } from "@/utils/uploadFiles";
import InlineBanner from "@/components/shared/InlineBanner";
import { ReferralInput } from "@/__generated__/graphql";
import Navbar from "@/components/layout/Navbar";

import styles from "./refer-patient.module.scss";
import "../../styles/components/landing-page.scss";

/* eslint-disable @typescript-eslint/no-loop-func */
/* eslint-disable no-await-in-loop */

pdfjs.GlobalWorkerOptions.workerPort = new Worker(
  new URL("pdfjs-dist/build/pdf.worker.mjs", import.meta.url),
  { type: "module" },
);

export type ConvertedFile = {
  fileName: string;
  files: File[];
};

export type ConversionResponse = {
  successfulConversions: ConvertedFile[];
  conversionFailedFilenames: string[]; // the filenames of the files that errored outs
};

export const SAVE_REFERRAL_MUTATION = gql(`
  mutation SaveReferral($referral: ReferralInput!) {
    saveReferral(referral: $referral) {
      referralId
      denialLetterLinks {
        fileExtension
        uploadUrl
      }
      medLetterLinks {
        fileExtension
        uploadUrl
      }
    }
  }
`);

const ReferPatient = () => {
  const [letterErrors, setLetterErrors] = useState<string[]>([]);
  const [medLetterErrors, setMedLetterErrors] = useState<string[]>([]);
  const [letterFiles, setLetterFiles] = useState<File[]>([]);
  const [medLetterFiles, setMedLetterFiles] = useState<File[]>([]);
  const navigate = useNavigate();

  const [saveReferral, { loading, error: saveError }] = useMutation(
    SAVE_REFERRAL_MUTATION,
  );

  const handleSubmit = async (formData: ReferralFormInput) => {
    let referralVars: ReferralInput = {
      firstName: formData.firstName as string,
      lastName: formData.lastName as string,
      patientEmail: formData.patientEmail as string,
      dateOfBirth: formData.dateOfBirth,
      state: formData.state as string,
      condition: formData.condition as string,
      medication: formData.medication as string,
      denialReason: formData.denialReason as string,
      message: formData.message as string,
    };

    if (letterFiles.length > 0) {
      referralVars = {
        ...referralVars,
        denialLetterExtensions: letterFiles.map(
          (file) => file.name.split(".").pop() ?? "",
        ),
      };
    }

    if (medLetterFiles.length > 0) {
      referralVars = {
        ...referralVars,
        medLetterExtensions: medLetterFiles.map(
          (file) => file.name.split(".").pop() ?? "",
        ),
      };
    }

    saveReferral({
      variables: {
        referral: referralVars,
      },
      async onCompleted(data) {
        const letterLinks = data.saveReferral.denialLetterLinks;
        if (
          letterFiles.length > 0 &&
          letterLinks?.length === letterFiles.length
        ) {
          await uploadFiles(letterFiles, letterLinks);
        }
        const { medLetterLinks } = data.saveReferral;
        if (
          medLetterFiles.length > 0 &&
          medLetterLinks?.length === medLetterFiles.length
        ) {
          await uploadFiles(medLetterFiles, medLetterLinks);
        }

        navigate("/", {
          state: {
            success: true,
          },
        });
      },
    });
  };

  /**
   *
   * @param uploadedFiles A list of files in which you want to convert pdfs to pngs
   * @returns A conversion response that contains the list of successfully converted files as well as a list of
   *          file names that errored out in the conversion process.
   */
  const convertPdfsToImg = async (
    uploadedFiles: File[],
  ): Promise<ConversionResponse> => {
    const filesAfterConversion: PromiseSettledResult<ConvertedFile>[] =
      await Promise.allSettled(
        uploadedFiles.map(async (file) => {
          if (file.type === "application/pdf") {
            try {
              const pdfDoc = await pdfjs.getDocument(await file.arrayBuffer())
                .promise;
              let imgFiles: File[] = [];
              for (let i = 1; i < pdfDoc.numPages + 1; i++) {
                await pdfDoc.getPage(i).then(async (page) => {
                  const scale = 1.5;
                  const viewport = page.getViewport({ scale });
                  const canvas = document.createElement("canvas");
                  const context = canvas.getContext("2d")!;

                  canvas.width = viewport.width;
                  canvas.height = viewport.height;

                  const renderContext = {
                    canvasContext: context,
                    viewport,
                  };

                  const fileName = `${file.name}-page-${i}.png`;
                  await page.render(renderContext).promise.then(async () => {
                    const blob: Blob = await new Promise((resolve, reject) => {
                      canvas.toBlob((canvasBlob) => {
                        if (canvasBlob) {
                          resolve(canvasBlob);
                        } else {
                          reject(new Error("Error converting pdf page to png"));
                        }
                      });
                    });

                    imgFiles = [...imgFiles, new File([blob], fileName)];
                  });
                });
              }
              return { fileName: file.name, files: imgFiles };
            } catch {
              throw new Error(`Invalid or corrupted filed: ${file.name}`);
            }
          }
          return { fileName: file.name, files: [file] };
        }),
      );

    const successfulFiles = filesAfterConversion
      .filter((promiseResult) => promiseResult.status === "fulfilled")
      .map((result) => result.value);
    const successfulFileNames = successfulFiles.map(
      (result) => result.fileName,
    );
    const filesNotConverted = uploadedFiles
      .map((file) => file.name)
      .filter((name) => !successfulFileNames.includes(name));
    return {
      successfulConversions: successfulFiles,
      conversionFailedFilenames: filesNotConverted,
    };
  };

  async function handleLetterUpload(uploadedFiles: File[]) {
    if (uploadedFiles) {
      setLetterErrors([]);
      setLetterFiles(uploadedFiles);
    }
    return undefined;
  }

  async function handleMedLetterUpload(
    uploadedFiles: File[],
  ): Promise<ConversionResponse | undefined> {
    if (uploadedFiles) {
      setMedLetterErrors([]);
      return convertPdfsToImg(uploadedFiles).then((result) => {
        const files = result.successfulConversions.flatMap(
          (conversion) => conversion.files,
        );
        setMedLetterFiles(files);
        setMedLetterErrors(
          result.conversionFailedFilenames.map(
            (f) => `Invalid or corrupt file: ${f}`,
          ),
        );
        return result;
      });
    }
    return undefined;
  }

  function handleBadLetterUpload(error: string[]) {
    setLetterErrors(error);
  }

  function handleBadMedLetterUpload(error: string[]) {
    setMedLetterErrors(error);
  }

  function handleLetterClose(message: string) {
    setLetterErrors(letterErrors.filter((error) => error !== message));
  }

  function handleMedLetterClose(message: string) {
    setMedLetterErrors(medLetterErrors.filter((error) => error !== message));
  }

  return (
    <Navbar>
      <Page title="Refer patient">
        <div className={styles.pageContainer}>
          <div className={styles.formContainer}>
            <h2 className={styles.formHeader}>{"Denial info"}</h2>
            <div className={styles.formSubheader}>
              <p className={styles.formSubheader__text}>
                {
                  "Tell us about your patient and we’ll send them an email inviting them to Claimable. See a sample "
                }
              </p>
              <p className={styles.formSubheader__text}>
                <a
                  className={styles.formSubheader__link}
                  target="_blank"
                  href="/images/product_illustrations/sample_email.png"
                >
                  {"email here."}
                </a>
              </p>
            </div>

            {saveError && (
              <>
                <InlineBanner
                  type="error"
                  message={saveError.message}
                  key={saveError.message}
                />
                <br />
              </>
            )}

            <ReferralForm formId="referralForm" onSubmit={handleSubmit} />
          </div>

          <div className={styles.uploadsContainer}>
            {letterErrors.map((error) => (
              <>
                <InlineBanner
                  type="error"
                  message={error}
                  dismissable
                  onDismiss={handleLetterClose}
                  key={error}
                />
                <br />
              </>
            ))}
            <UploadDragDrop
              sectionHeader="Upload denial letter"
              headerText="Drag your files here or click to upload"
              subText="Ensure all pages of your denial letter are included. Maximum file size 20MB"
              onDragDropAction={handleLetterUpload}
              onBadItemUpload={handleBadLetterUpload}
            />
            <div className={styles.disclaimerContainer}>
              <div className={styles.uploadDisclaimer}>
                <div className={styles.icon}>
                  <FontAwesomeIcon icon={faLightbulb} title="Information" />
                </div>
                <div className={styles.copy}>
                  <p className={styles.title}>{"Why this matters"}</p>
                  <p>
                    {
                      "We pull relevant information from the denial letter to give your patient a head start. The denial letter is not sent with the appeal. While strongly recommended, it's optional."
                    }
                  </p>
                </div>
              </div>
            </div>
            {medLetterErrors.map((error) => (
              <>
                <InlineBanner
                  type="error"
                  message={error}
                  dismissable
                  onDismiss={handleMedLetterClose}
                  key={error}
                />
                <br />
              </>
            ))}
            <UploadDragDrop
              sectionHeader="Upload letter of medical necessity"
              headerText="Drag your files here or click to upload"
              subText="Ensure all pages of your letter of medical necessity are included. Maximum file size 20MB"
              onDragDropAction={handleMedLetterUpload}
              onBadItemUpload={handleBadMedLetterUpload}
            />

            <div className={styles.disclaimerContainer}>
              <div className={styles.uploadDisclaimer}>
                <div className={styles.icon}>
                  <FontAwesomeIcon icon={faLightbulb} title="Information" />
                </div>
                <div className={styles.copy}>
                  <p className={styles.title}>{"Why this matters"}</p>
                  <p>
                    {
                      "Appeals with a letter of medical necessity are more successful that ones without. Letters of medical necessity are included in the appeal package."
                    }
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>

        <div
          className={classnames(
            "send-referral-button-container",
            styles.sendReferralButton,
          )}
        >
          <button
            type="submit"
            form="referralForm"
            className={classnames("btn", "dark_double_right_arrow", {
              loading_button: loading,
            })}
          >
            {loading ? (
              <div className="loading_indicator_container">
                <div className="loading_indicator_text">{"Loading..."}</div>
                <div className="loading_indicator">
                  <Loader color="loader_light_volt" width="36" height="36" />
                </div>
              </div>
            ) : (
              <>
                {"Send Referral"}
                <span className="arrow" />
              </>
            )}
          </button>
        </div>
      </Page>
    </Navbar>
  );
};

export default ReferPatient;
